import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../../api/axiosClient';
import CountdownTimer from '../admin/CountdownTimer';
import { LayoutDashboard, LogOut, Settings, Users, FileText } from 'lucide-react';

export default function SandboxDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSandboxData = async () => {
      try {
        const res = await axiosClient.get('/trials/sandbox/settings');
        setData(res.data);
      } catch (err) {
        if (err.response?.data?.error === 'Trial is pending admin approval.') {
          setError('Your trial request has been submitted and is pending admin approval. You will receive an email once your isolated sandbox is ready!');
        } else if (err.response?.status === 401 || err.response?.status === 403) {
          setError('Your trial has expired or is invalid.');
          localStorage.removeItem('sandboxToken');
        } else {
          setError('Failed to load sandbox data. Please try again.');
        }
      }
    };

    if (localStorage.getItem('sandboxToken')) {
      fetchSandboxData();
    } else {
      navigate('/sandbox-login');
    }
  }, [navigate]);

  if (error) {
    const isPending = error.includes('pending');
    
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <div className={`bg-white p-8 rounded-2xl shadow-xl max-w-md text-center border ${isPending ? 'border-primary-100' : 'border-red-100'}`}>
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isPending ? 'bg-primary-100 text-primary-600' : 'bg-red-100 text-red-600'}`}>
            {isPending ? <LayoutDashboard size={32} /> : <LogOut size={32} />}
          </div>
          <h2 className="text-2xl font-display font-bold text-slate-900 mb-2">
            {isPending ? 'Trial Pending Approval' : 'Access Denied'}
          </h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <button onClick={() => navigate('/')} className="bg-primary-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-700">
            Return Home
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mb-4"></div>
          <p className="text-slate-500 font-medium">Connecting to your isolated instance...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 font-sans flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-slate-300 flex flex-col">
        <div className="p-6 border-b border-slate-800">
          <h2 className="text-xl font-display font-bold text-white tracking-wide">SchoolOS</h2>
          <p className="text-xs text-primary-400 mt-1 uppercase tracking-wider font-semibold">Sandbox Environment</p>
        </div>
        <nav className="flex-1 p-4 space-y-2">
          <a href="#" className="flex items-center px-4 py-3 bg-primary-600/10 text-primary-400 rounded-xl font-medium">
            <LayoutDashboard size={20} className="mr-3" /> Dashboard
          </a>
          <a href="#" className="flex items-center px-4 py-3 hover:bg-slate-800 rounded-xl transition-colors font-medium">
            <Users size={20} className="mr-3" /> Students & Staff
          </a>
          <a href="#" className="flex items-center px-4 py-3 hover:bg-slate-800 rounded-xl transition-colors font-medium">
            <FileText size={20} className="mr-3" /> Reports
          </a>
          <a href="#" className="flex items-center px-4 py-3 hover:bg-slate-800 rounded-xl transition-colors font-medium">
            <Settings size={20} className="mr-3" /> Configuration
          </a>
        </nav>
        <div className="p-4 border-t border-slate-800">
          <button onClick={() => { localStorage.removeItem('sandboxToken'); navigate('/'); }} className="flex items-center px-4 py-2 text-slate-400 hover:text-white w-full transition-colors">
            <LogOut size={18} className="mr-3" /> End Session
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col">
        {/* Top Banner - Trial Expiry */}
        <div className="bg-primary-600 text-white px-8 py-3 flex justify-between items-center shadow-md z-10 relative">
          <div className="flex items-center">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse mr-3"></span>
            <span className="font-medium text-sm">You are viewing a live, isolated database instance.</span>
          </div>
          <div className="text-sm font-medium bg-black/20 px-4 py-1.5 rounded-full flex items-center">
            Trial expires in: <span className="ml-2 bg-white px-2 py-0.5 rounded text-primary-700 font-bold tracking-wider"><CountdownTimer expiresAt={data.expiresAt} /></span>
          </div>
        </div>

        {/* Dashboard Area */}
        <div className="p-8 flex-1 overflow-auto">
          <header className="mb-8">
            <h1 className="text-3xl font-display font-bold text-slate-900 mb-2">Welcome, {data.settings?.school_name || 'Organization'}!</h1>
            <p className="text-slate-600">This is your dedicated ERP workspace. Any changes made here are isolated to your specific trial database.</p>
          </header>

          <div className="grid grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 col-span-2">
              <h3 className="font-display font-bold text-slate-800 mb-4 border-b border-slate-100 pb-2">System Logs</h3>
              <ul className="space-y-3 text-sm text-slate-600 font-mono bg-slate-50 p-4 rounded-xl border border-slate-100 h-64 overflow-y-auto">
                <li>[System] Schema successfully cloned from master template.</li>
                <li>[System] Dedicated tenant isolated environment secured.</li>
                <li>[Data] Sample configuration seeded successfully.</li>
                <li>[Info] Waiting for user actions...</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
              <h3 className="font-display font-bold text-slate-800 mb-4 border-b border-slate-100 pb-2">Instance Meta</h3>
              <div className="space-y-4">
                <div>
                  <div className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1">Status</div>
                  <div className="font-medium text-emerald-600 flex items-center"><span className="w-2 h-2 rounded-full bg-emerald-500 mr-2"></span> Active & Ready</div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1">Isolation Type</div>
                  <div className="font-medium text-slate-800">PostgreSQL Schema-Level</div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-1">Database Size</div>
                  <div className="font-medium text-slate-800">8.4 MB (Sample Data)</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
